import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { contextContractSelector } from '../../../redux/context/contract/selectors'
import { requireContractProductAtLeast } from '../../../components/Misc/RestrictedProduct'
import { ContractProduct } from '../../../types/contract/Contract'
import { useCtxReportableFiscalYears } from './useCtxReportableFiscalYears'
import { fiscalYearHasColumns } from '../utils'

/**
 * Fiscal years chosen to be shown as table columns. Only affects columns — data fetching
 * and other consumers keep using all reportable fiscal years.
 *
 * - selectable: reportable fiscal years that can produce a column with the current data types
 *   (e.g. a future, non-budgetable fiscal year has none)
 * - selected: fiscal years used to filter columns; all reportable when nothing is chosen
 */
export const useSelectedFiscalYears = () => {
  const companyId = useSelector(contextCompanyIdSelector)
  const { fiscalYearIdsByCompany, dataTypes } = useSelector(filtersSelector)
  const contract = useSelector(contextContractSelector)
  const fiscalYearIds = companyId ? fiscalYearIdsByCompany?.[companyId] : undefined
  const { data: reportable = [] } = useCtxReportableFiscalYears()
  const budgetCategoriesEnabled = !!(
    contract?.product && requireContractProductAtLeast(contract.product, ContractProduct.BASIC)
  )

  return useMemo(() => {
    const selectable = reportable.filter(fy => fiscalYearHasColumns(fy, dataTypes, budgetCategoriesEnabled))
    const chosen = fiscalYearIds?.length ? reportable.filter(fy => fiscalYearIds.includes(fy.id)) : []
    const chosenSelectable = selectable.filter(fy => chosen.includes(fy))

    // Nothing chosen or stale ids (e.g. after contract change) -> columns as without this feature
    if (chosenSelectable.length === 0) {
      return { selectable, selected: reportable, selectedSelectable: selectable, isAllSelected: true }
    }
    return {
      selectable,
      selected: chosen,
      selectedSelectable: chosenSelectable,
      isAllSelected: chosenSelectable.length === selectable.length
    }
  }, [fiscalYearIds, reportable, dataTypes, budgetCategoriesEnabled])
}
